<template>
  <div v-loading="loading" class="tc-form-wrap">
    <el-form ref="mainForm" :model="formData" :rules="mainRules" label-width="120px" size="small" :disabled="isDetails()">
      <h4 class="tc-sec-title">合同基础信息</h4>
      <el-row :gutter="12">
        <el-col :span="8">
          <el-form-item label="合同编号">
            <el-input v-model="formData.contractNo" disabled placeholder="保存后自动生成" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="合同类型" prop="contractType">
            <el-select v-model="formData.contractType" placeholder="请选择" style="width:100%" @change="onContractTypeChange">
              <el-option v-for="o in contractTypeOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="合同份数" prop="copyCount">
            <el-select v-model="formData.copyCount" placeholder="请选择" style="width:100%">
              <el-option v-for="o in copyCountOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="开始日期" prop="contractStartTime">
            <el-date-picker v-model="formData.contractStartTime" type="date" value-format="yyyy-MM-dd" style="width:100%" @change="syncServicePeriod" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="结束日期" prop="contractEndTime">
            <el-date-picker v-model="formData.contractEndTime" type="date" value-format="yyyy-MM-dd" style="width:100%" @change="syncServicePeriod" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="服务期限">
            <el-input :value="servicePeriodDisplay" readonly placeholder="根据起止日期计算（年）" />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <avue-form ref="custAvueForm" v-model="formData" :option="optionCust" class="tc-cust-avue" />
        </el-col>
        <el-col :span="8">
          <el-form-item label="合同名称" prop="contractName">
            <el-input v-model="formData.contractName" maxlength="64" show-word-limit />
          </el-form-item>
        </el-col>
        <el-col :span="8">
          <el-form-item label="签订日期" prop="contractSignDate">
            <el-date-picker v-model="formData.contractSignDate" type="date" value-format="yyyy-MM-dd" style="width:100%" />
          </el-form-item>
        </el-col>
      </el-row>

      <h4 class="tc-sec-title">合同结算信息</h4>

      <div v-if="!isDetails()" class="tc-table-actions">
        <el-button type="primary" size="small" icon="el-icon-plus" @click="addSettlementRow">添加行</el-button>
      </div>
      <el-table :data="formData.list" border size="small" class="tc-settle-table">
        <el-table-column label="费目" min-width="140" align="center">
          <template slot-scope="{ row }">
            <span v-if="isDetails()">{{ feeLabel(row.feeItem) }}</span>
            <el-select v-else v-model="row.feeItem" placeholder="费目" style="width:100%" @change="onFeeChange(row)">
              <el-option v-for="o in feeOptionsForCurrentContract" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="计量单位" min-width="100" align="center">
          <template slot-scope="{ row }">
            <span v-if="isDetails()">{{ unitLabel(row.measureUnit) }}</span>
            <el-select v-else v-model="row.measureUnit" placeholder="单位" :disabled="!row.feeItem" style="width:100%" @change="recalcSettleRow(row)">
              <el-option v-for="o in unitOptionsForRow(row)" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="品名(煤种)" min-width="160" align="center">
          <template slot-scope="{ row }">
            <AutocompleteReset
              v-if="needCoal(row) && !isDetails()"
              v-model="row.cargoName"
              type-use="cargoName"
              :form-data="row"
              style="width:100%"
            />
            <span v-else>{{ row.cargoName || "—" }}</span>
          </template>
        </el-table-column>
        <el-table-column label="货位" min-width="200" align="center">
          <template slot-scope="{ row }">
            <div v-if="needStack(row) && !isDetails()" class="tc-stack-cell">
              <div class="tc-stack-summary" :title="stackRowDisplay(row)">
                {{ stackSelectSummary(row) }}
              </div>
              <el-button size="mini" type="primary" plain @click="openStackDialog(row)">选择</el-button>
            </div>
            <el-select
              v-if="needStack(row) && !isDetails()"
              v-model="row.storeSubId"
              multiple
              collapse-tags
              filterable
              clearable
              placeholder="未关联合同的堆位（仅仓储服务费）"
              style="width:100%"
              @change="onStackSubChange(row)"
            >
              <el-option
                v-for="s in unboundStacks"
                :key="s.id"
                :label="stackLabel(s)"
                :value="s.id"
                :disabled="isStackSelectedByOtherRow(row, s.id)"
              />
            </el-select>
            <span v-else>{{ stackRowDisplay(row) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="单价" width="110" align="center">
          <template slot-scope="{ row }">
            <el-input-number
              v-model="row.unitPrice"
              :min="0"
              :precision="2"
              :step="0.01"
              :controls="false"
              :disabled="isDetails()"
              style="width:100%"
              @change="recalcSettleRow(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="数量" width="110" align="center">
          <template slot-scope="{ row }">
            <el-input-number
              v-model="row.contractWeight"
              :min="0"
              :precision="0"
              :step="1"
              :controls="false"
              :disabled="isDetails()"
              style="width:100%"
              @change="recalcSettleRow(row)"
            />
          </template>
        </el-table-column>
        <el-table-column label="税率(‰)" width="100" align="center">
          <template slot-scope="{ row }">
            <el-select
              v-if="!isDetails()"
              v-model="row.taxRate"
              placeholder="税率"
              style="width:100%"
              @change="recalcSettleRow(row)"
            >
              <el-option label="6‰" :value="0.6" />
              <el-option label="13‰" :value="1.3" />
            </el-select>
            <span v-else>{{ formatTaxRateCell(row.taxRate) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="不含税金额" width="120" align="center">
          <template slot-scope="{ row }">
            <span class="tc-money-cell">{{ formatMoneyCell(row.amountExclTax) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="价税合计" width="120" align="center">
          <template slot-scope="{ row }">
            <span class="tc-money-cell">{{ formatMoneyCell(row.amountInclTax) }}</span>
          </template>
        </el-table-column>
        <el-table-column label="结算方式" min-width="120" align="center">
          <template slot-scope="{ row }">
            <span v-if="isDetails()">{{ settleLabel(row.settleMethod) }}</span>
            <el-select v-else v-model="row.settleMethod" placeholder="请选择" style="width:100%">
              <el-option v-for="o in settleMethodOptions" :key="o.value" :label="o.label" :value="o.value" />
            </el-select>
          </template>
        </el-table-column>
        <el-table-column label="备注" min-width="100" align="center">
          <template slot-scope="{ row }">
            <el-input v-model="row.lineRemark" :disabled="isDetails()" />
          </template>
        </el-table-column>
        <el-table-column v-if="!isDetails()" label="操作" width="70" align="center" fixed="right">
          <template slot-scope="{ $index }">
            <el-button type="text" class="tc-del" @click="removeRow($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <el-dialog
        title="选择货位"
        :visible.sync="stackDialogVisible"
        append-to-body
        width="920px"
        class="tc-stack-dialog"
      >
        <div class="tc-stack-toolbar">
          <el-select v-model="stackFilterStoreNo" clearable placeholder="煤棚" size="small" class="tc-stack-filter">
            <el-option
              v-for="item in stackStoreOptions"
              :key="item.storeNo"
              :label="item.label"
              :value="item.storeNo"
            />
          </el-select>
          <el-input
            v-model="stackKeyword"
            clearable
            size="small"
            prefix-icon="el-icon-search"
            placeholder="搜索货位名称/编码"
            class="tc-stack-search"
          />
          <div class="tc-stack-total">
            已选 {{ stackDialogSelectedIds.length }} 个，面积 {{ stackDialogTotalArea }}m2
          </div>
        </div>
        <el-table :data="filteredStackOptions" border size="small" height="420" class="tc-stack-table">
          <el-table-column width="60" align="center">
            <template slot-scope="{ row }">
              <el-checkbox
                :value="isStackChecked(row)"
                :disabled="isStackDisabledInDialog(row)"
                @change="(checked) => toggleStackInDialog(row, checked)"
              />
            </template>
          </el-table-column>
          <el-table-column prop="storeName" label="煤棚" min-width="120" show-overflow-tooltip />
          <el-table-column prop="locationName" label="货位" min-width="110" show-overflow-tooltip />
          <el-table-column prop="locationCode" label="货位编码" min-width="260" show-overflow-tooltip />
          <el-table-column label="面积(m2)" width="100" align="right">
            <template slot-scope="{ row }">{{ stackArea(row) }}</template>
          </el-table-column>
          <el-table-column prop="stockAmount" label="库存" width="110" align="right" />
          <el-table-column label="状态" width="90" align="center">
            <template slot-scope="{ row }">
              <el-tag v-if="isStackDisabledInDialog(row)" size="mini" type="info">已选</el-tag>
              <el-tag v-else size="mini" type="success">可选</el-tag>
            </template>
          </el-table-column>
        </el-table>
        <div slot="footer" class="dialog-footer">
          <el-button @click="clearStackDialogSelection">清空</el-button>
          <el-button @click="stackDialogVisible = false">取消</el-button>
          <el-button type="primary" @click="confirmStackDialog">确定</el-button>
        </div>
      </el-dialog>

      <h4 class="tc-sec-title">合同附件</h4>
      <div v-for="grp in attachmentGroups" :key="grp.title" class="tc-upload-group">
        <div class="tc-upload-group-title">{{ grp.title }}</div>
        <el-row :gutter="12" type="flex" class="tc-upload-row">
          <el-col v-for="item in grp.items" :key="item.key" :span="12" :lg="8" class="tc-upload-col">
            <div
              class="tc-upload-card"
              :class="{ 'tc-upload--hide-trigger': !showContractAttachUploadBtn(item) }"
            >
              <div class="tc-upload-label">
                {{ item.label }}
                <span v-if="item.required" class="tc-req">必传</span>
              </div>
              <el-upload
                :action="uploadAction"
                :headers="uploadHeaders"
                :file-list="attFiles[item.key] || []"
                :disabled="isDetails()"
                :limit="attachLimit(item)"
                :multiple="item.multiple"
                accept=".jpg,.jpeg,.png,.gif,.bmp,.webp,.pdf,.doc,.docx,image/*"
                :before-upload="beforeContractAttachUpload"
                :on-exceed="() => onAttachExceed(item)"
                :on-error="onContractAttachError"
                :on-preview="downloadContractAttachment"
                :on-success="(res, file, fl) => onAttachSuccess(item.key, res, file, fl, item.multiple)"
                :on-remove="(file, fl) => onAttachRemove(item.key, file, fl)"
              >
                <el-button v-if="showContractAttachUploadBtn(item)" size="small" type="primary" plain>上传</el-button>
              </el-upload>
            </div>
          </el-col>
        </el-row>
      </div>
    </el-form>

    <div v-if="!isDetails()" class="dialog-footer tc-footer">
      <el-button plain :loading="loading" @click="saveDraft">暂存</el-button>
      <el-button type="primary" :loading="loading" @click="submitForm">提交</el-button>
      <el-button @click="$emit('handleClose')">取消</el-button>
    </div>
  </div>
</template>

<script>
import { add, update, getDetail, listUnboundStacks } from "../api";
import { getTodayDateStr } from "@/util/date";
import { getToken } from "@/util/auth";
import { rulesInput } from "@/basic-common/util/common";
import AutocompleteReset from "@/basic-common/components/AutocompleteReset.vue";
import {
  FEE,
  UNIT,
  FEE_OPTIONS,
  SETTLE_OPTIONS,
  COPY_OPTIONS,
  UTIL_FEE_VALUES,
  feeLabel as mapFeeLabel,
  unitLabel as mapUnitLabel,
  settleLabel as mapSettleLabel,
} from "../transportContractDict.js";

/** 合同附件允许的后缀（小写），与 accept / before-upload 一致 */
const CONTRACT_ATTACH_EXT = new Set(["jpg", "jpeg", "png", "gif", "bmp", "webp", "pdf", "doc", "docx"]);
const CONTRACT_ATTACH_MAX_SIZE_MB = 20;
const CONTRACT_STATUS_ACTIVE = 1;
const CONTRACT_STATUS_DRAFT = 2;

const ATTACHMENT_GROUPS = [
  {
    title: "合同信息",
    items: [
      { key: "contractScan", label: "合同原件扫描件", required: true, multiple: false },
      { key: "layoutPlan", label: "货位平面图", required: false, multiple: false },
      { key: "supplement", label: "补充协议", required: false, multiple: false },
      { key: "otherContract", label: "其他附件", required: false, multiple: true },
    ],
  },
  {
    title: "客户资质",
    items: [
      { key: "license", label: "营业执照", required: true, multiple: false },
      { key: "legalIdCardFront", label: "法人身份证正面", required: true, multiple: false },
      { key: "legalIdCardBack", label: "法人身份证反面", required: true, multiple: false },
      { key: "bankProof", label: "银行开户证明", required: true, multiple: false },
    ],
  },
  {
    title: "生产运输资质",
    items: [
      { key: "roadTransportLic", label: "道路运输许可证", required: false, multiple: false },
      { key: "specialEquip", label: "特种设备作业证", required: false, multiple: false },
      { key: "specialTransportLic", label: "特种运输许可证", required: false, multiple: false },
      { key: "safeProductionLic", label: "安全生产许可证", required: false, multiple: false },
      { key: "otherQualification", label: "其他", required: false, multiple: true },
    ],
  },
];

export default {
  name: "TransportContractForm",
  components: { AutocompleteReset },
  props: {
    transmitData: {
      type: Object,
      required: true,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      unboundStacks: [],
      stackDialogVisible: false,
      stackDialogRow: null,
      stackDialogSelectedIds: [],
      stackFilterStoreNo: "",
      stackKeyword: "",
      /** 各附件 key → { name, url, uid }[] */
      attFiles: {},
      attachmentGroups: ATTACHMENT_GROUPS,
      settleMethodOptions: SETTLE_OPTIONS,
      /** 与库表 contract_type 整型一致：1/2 */
      contractTypeOptions: [
        { label: "仓储服务合同", value: 1 },
        { label: "翻卸服务合同", value: 2 },
      ],
      copyCountOptions: COPY_OPTIONS,
      formData: {
        list: [],
        contractType: null,
        copyCount: null,
        /** 服务期限（年），与接口 servicePeriodYears、库列 service_period_years 一致 */
        servicePeriodYears: null,
        contractName: "",
        custNo: "",
        contractStartTime: "",
        contractEndTime: "",
        contractSignDate: "",
        annexPath: "",
        ext: "",
        businessType: 1,
      },
      mainRules: {
        contractType: [{ required: true, message: "请选择合同类型", trigger: "change" }],
        copyCount: [{ required: true, message: "请选择合同份数", trigger: "change" }],
        contractName: [{ required: true, message: "请输入合同名称", trigger: "blur" }],
        contractStartTime: [{ required: true, message: "请选择开始日期", trigger: "change" }],
        contractEndTime: [
          { required: true, message: "请选择结束日期", trigger: "change" },
          { validator: this.validateContractEndTime, trigger: "change" },
        ],
      },
      uploadAction: "/produce/api/blade-resource/oss/endpoint/put-file-attach",
    };
  },
  computed: {
    uploadHeaders() {
      const t = getToken();
      return t ? { Authorization: "Bearer " + t, "Blade-Auth": "Bearer " + t } : {};
    },
    stackStoreOptions() {
      const map = {};
      this.unboundStacks.forEach((s) => {
        const key = s.storeNo || "";
        if (!key || map[key]) return;
        map[key] = {
          storeNo: key,
          label: [s.storeName, s.storeNo].filter(Boolean).join(" "),
        };
      });
      return Object.keys(map).map((k) => map[k]);
    },
    filteredStackOptions() {
      const keyword = String(this.stackKeyword || "").trim().toLowerCase();
      return this.unboundStacks.filter((s) => {
        if (this.stackFilterStoreNo && String(s.storeNo) !== String(this.stackFilterStoreNo)) return false;
        if (!keyword) return true;
        return [s.storeName, s.storeNo, s.locationName, s.locationCode]
          .filter(Boolean)
          .some((v) => String(v).toLowerCase().indexOf(keyword) >= 0);
      });
    },
    stackDialogTotalArea() {
      return this.stackDialogSelectedIds.reduce((sum, id) => {
        return sum + this.stackArea(this.findStackById(id));
      }, 0);
    },
    /** 与日运输计划 /basic-info/daytransportplan 客户列一致：remote + dicUrl + props */
    servicePeriodDisplay() {
      const y = this.formData.servicePeriodYears;
      if (y == null || y === "" || !Number.isFinite(Number(y))) return "";
      return Number(y) + "年";
    },
    optionCust() {
      return {
        ...this.$store.state.formOption,
        submitBtn: false,
        emptyBtn: false,
        labelWidth: 120,
        detail: this.isDetails(),
        column: [
          {
            label: "客户",
            prop: "custNo",
            span: 24,
            type: "select",
            remote: true,
            dicUrl: "/api/produce/blade-dept/select/distinct/select",
            props: {
              label: "deptName",
              value: "id",
            },
            rules: rulesInput(2),
          },
        ],
      };
    },
    feeOptionsForCurrentContract() {
      // if (this.formData.contractType === 1) {
      //   return FEE_OPTIONS.filter((o) => [FEE.WAREHOUSE, ...UTIL_FEE_VALUES].indexOf(o.value) >= 0);
      // }
      // if (this.formData.contractType === 2) {
      //   return FEE_OPTIONS.filter((o) => [FEE.LOAD, FEE.RAIL_UNLOAD, FEE.TRUCK_PIT].indexOf(o.value) >= 0);
      // }
      return FEE_OPTIONS;
    },
  },
  created() {
    const { type, id } = this.transmitData;
    if (type === "add") {
      this.formData.list = [this.emptyRow()];
      this.setDefaultDateFields();
      this.loadUnboundStacks();
    } else if (id) {
      this.getDetail(id);
    }
  },
  methods: {
    feeLabel(v) {
      return mapFeeLabel(v);
    },
    unitLabel(v) {
      return mapUnitLabel(v);
    },
    settleLabel(v) {
      return mapSettleLabel(v);
    },
    emptyRow() {
      return {
        feeItem: null,
        measureUnit: null,
        storeSubId: [],
        stockCode: null,
        stockName: null,
        locationCode: null,
        locationName: null,
        cargoNo: "",
        cargoName: "",
        contractWeight: null,
        unitPrice: null,
        taxRate: 0.6,
        amountExclTax: null,
        amountInclTax: null,
        settleMethod: 1,
        lineRemark: "",
      };
    },
    validateContractEndTime(rule, value, callback) {
      const start = this.formData.contractStartTime;
      if (!start || !value) {
        callback();
        return;
      }
      const ds = new Date(String(start).replace(/-/g, "/"));
      const de = new Date(String(value).replace(/-/g, "/"));
      if (Number.isNaN(ds.getTime()) || Number.isNaN(de.getTime())) {
        callback();
        return;
      }
      if (de <= ds) {
        callback(new Error("结束日期必须晚于开始日期"));
        return;
      }
      callback();
    },
    formatMoneyCell(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return n.toFixed(2);
    },
    formatTaxRateCell(v) {
      if (v == null || v === "") return "—";
      const n = Number(v);
      if (!Number.isFinite(n)) return "—";
      return (n * 10).toFixed(0) + "‰";
    },
    isSupportedTaxRate(v) {
      const n = Number(v);
      return Number.isFinite(n) && (Math.abs(n - 0.6) < 0.0001 || Math.abs(n - 1.3) < 0.0001);
    },
    isFeeAllowedForCurrentContract(feeItem) {
      if (feeItem == null || feeItem === "") return true;
      return this.feeOptionsForCurrentContract.some((o) => o.value === feeItem);
    },
    resetSettlementRow(row) {
      const blank = this.emptyRow();
      Object.keys(blank).forEach((k) => {
        this.$set(row, k, blank[k]);
      });
    },
    onContractTypeChange() {
      const changed = (this.formData.list || []).filter((row) => row.feeItem != null && row.feeItem !== "" && !this.isFeeAllowedForCurrentContract(row.feeItem));
      changed.forEach((row) => this.resetSettlementRow(row));
      if (changed.length) {
        this.$message.warning("已清空与当前合同类型不匹配的结算明细");
      }
    },
    recalcSettleRow(row) {
      const p = row.unitPrice;
      const q = row.contractWeight;
      const r = row.taxRate;
      const pn = Number(p);
      const qn = Number(q);
      const rn = Number(r);
      if (p == null || q == null || r == null || !Number.isFinite(pn) || !Number.isFinite(qn) || !Number.isFinite(rn)) {
        this.$set(row, "amountExclTax", null);
        this.$set(row, "amountInclTax", null);
        return;
      }
      const excl = Number((pn * qn).toFixed(2));
      const incl = Number((excl * (1 + rn / 100)).toFixed(2));
      this.$set(row, "amountExclTax", excl);
      this.$set(row, "amountInclTax", incl);
    },
    async loadUnboundStacks() {
      try {
        const res = await listUnboundStacks();
        const body = res && res.data ? res.data : {};
        const list = body.data != null ? body.data : [];
        this.unboundStacks = Array.isArray(list) ? list : [];
      } catch (e) {
        this.unboundStacks = [];
      }
    },
    stackLabel(s) {
      if (!s) return "";
      const shed = [s.storeName, s.storeNo].filter(Boolean).join(" ");
      const loc = [s.locationName, s.locationCode].filter(Boolean).join(" ");
      const area = this.stackArea(s);
      return [shed, loc, area ? area + "m2" : ""].filter(Boolean).join(" / ");
    },
    normalizeStackIds(value) {
      if (Array.isArray(value)) {
        return value.filter((v) => v !== null && v !== undefined && v !== "");
      }
      if (value === null || value === undefined || value === "") return [];
      return [value];
    },
    findStackById(id) {
      return this.unboundStacks.find((x) => String(x.id) === String(id));
    },
    stackArea(s) {
      if (!s) return 0;
      const w = Number(s.layoutW);
      const h = Number(s.layoutH);
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) return 0;
      return Math.round(w * h);
    },
    stackLabelById(id) {
      const ids = this.normalizeStackIds(id);
      if (!ids.length) return "—";
      return ids.map((item) => {
        const s = this.findStackById(item);
        return s ? this.stackLabel(s) : "#" + item;
      }).join("; ");
    },
    /** 详情展示：优先接口返回的煤棚/堆位编码名称（与 transport_contract_ext 落库一致） */
    stackSelectSummary(row) {
      const ids = this.normalizeStackIds(row && row.storeSubId);
      if (!ids.length) return "未选择货位";
      return "已选 " + ids.length + " 个 / " + this.stackSelectedArea(ids) + "m2";
    },
    stackSelectedArea(ids) {
      return this.normalizeStackIds(ids).reduce((sum, id) => {
        return sum + this.stackArea(this.findStackById(id));
      }, 0);
    },
    openStackDialog(row) {
      this.stackDialogRow = row;
      this.stackDialogSelectedIds = this.normalizeStackIds(row.storeSubId);
      this.stackKeyword = "";
      this.stackFilterStoreNo = "";
      this.stackDialogVisible = true;
    },
    isStackChecked(row) {
      return this.stackDialogSelectedIds.some((id) => String(id) === String(row.id));
    },
    isStackDisabledInDialog(row) {
      return this.stackDialogRow ? this.isStackSelectedByOtherRow(this.stackDialogRow, row.id) : false;
    },
    toggleStackInDialog(row, checked) {
      const id = row.id;
      const next = this.stackDialogSelectedIds.filter((item) => String(item) !== String(id));
      if (checked) next.push(id);
      this.stackDialogSelectedIds = next;
    },
    clearStackDialogSelection() {
      this.stackDialogSelectedIds = [];
    },
    confirmStackDialog() {
      if (!this.stackDialogRow) return;
      this.$set(this.stackDialogRow, "storeSubId", this.stackDialogSelectedIds.slice());
      this.onStackSubChange(this.stackDialogRow);
      this.stackDialogVisible = false;
    },
    stackRowDisplay(row) {
      if (!row) return "—";
      const shed = [row.stockName, row.stockCode].filter(Boolean).join(" ");
      const loc = [row.locationName, row.locationCode].filter(Boolean).join(" ");
      const t = [shed, loc].filter(Boolean).join(" / ");
      return t || this.stackLabelById(row.storeSubId);
    },
    onStackSubChange(row) {
      const ids = this.normalizeStackIds(row.storeSubId);
      this.$set(row, "storeSubId", ids);
      if (!ids.length) {
        this.$set(row, "stockCode", null);
        this.$set(row, "stockName", null);
        this.$set(row, "locationCode", null);
        this.$set(row, "locationName", null);
        if (this.needStack(row)) {
          this.$set(row, "contractWeight", null);
          this.recalcSettleRow(row);
        }
        return;
      }
      const stacks = ids.map((id) => this.findStackById(id)).filter(Boolean);
      if (stacks.length) {
        this.$set(row, "stockCode", Array.from(new Set(stacks.map((s) => s.storeNo).filter((v) => v != null && v !== ""))).join(","));
        this.$set(row, "stockName", Array.from(new Set(stacks.map((s) => s.storeName).filter((v) => v != null && v !== ""))).join(","));
        this.$set(row, "locationCode", stacks.map((s) => s.locationCode).filter((v) => v != null && v !== "").join(","));
        this.$set(row, "locationName", stacks.map((s) => s.locationName).filter((v) => v != null && v !== "").join(","));
        if (this.needStack(row)) {
          const totalArea = stacks.reduce((sum, s) => sum + this.stackArea(s), 0);
          if (totalArea > 0) {
            this.$set(row, "contractWeight", totalArea);
          }
          this.recalcSettleRow(row);
        }
      }
    },
    needCoal(row) {
      const f = row.feeItem;
      if (f == null || f === "") return false;
      return UTIL_FEE_VALUES.indexOf(f) === -1;
    },
    needStack(row) {
      return row.feeItem === FEE.WAREHOUSE;
    },
    unitOptionsForRow(row) {
      const f = row.feeItem;
      if (f == null || f === "") return [];
      if (f === FEE.WAREHOUSE) {
        return [{ label: "平方米", value: UNIT.SQUARE_METER }];
      }
      if (f === FEE.RAIL_UNLOAD) {
        return [{ label: "吨", value: 1 }];
      }
      if (f === FEE.TRUCK_PIT || f === FEE.LOAD) {
        return [
          { label: "吨", value: 1 },
          { label: "车数", value: 2 },
        ];
      }
      if (UTIL_FEE_VALUES.indexOf(f) >= 0) {
        return [{ label: "立方", value: 3 }];
      }
      return [];
    },
    onFeeChange(row) {
      const opts = this.unitOptionsForRow(row);
      row.measureUnit = opts.length === 1 ? opts[0].value : null;
      if (!this.needCoal(row)) {
        row.cargoNo = "";
        row.cargoName = "";
      }
      if (!this.needStack(row)) {
        row.storeSubId = null;
        row.stockCode = null;
        row.stockName = null;
        row.locationCode = null;
        row.locationName = null;
        row.contractWeight = null;
        this.recalcSettleRow(row);
      } else {
        row.storeSubId = this.normalizeStackIds(row.storeSubId);
      }
      this.recalcSettleRow(row);
    },
    addSettlementRow() {
      const row = this.emptyRow();
      this.formData.list.push(row);
      this.recalcSettleRow(row);
    },
    removeRow(i) {
      this.formData.list.splice(i, 1);
      if (!this.formData.list.length) {
        const row = this.emptyRow();
        this.formData.list.push(row);
        this.recalcSettleRow(row);
      }
    },
    setDefaultDateFields() {
      const today = getTodayDateStr();
      ["contractStartTime", "contractEndTime", "contractSignDate"].forEach((f) => {
        if (!this.formData[f]) this.$set(this.formData, f, today);
      });
      this.syncServicePeriod();
    },
    syncServicePeriod() {
      const s = this.formData.contractStartTime;
      const e = this.formData.contractEndTime;
      if (!s || !e) {
        this.formData.servicePeriodYears = null;
        return;
      }
      const ds = new Date(s.replace(/-/g, "/"));
      const de = new Date(e.replace(/-/g, "/"));
      if (isNaN(ds) || isNaN(de) || de <= ds) {
        this.formData.servicePeriodYears = null;
        return;
      }
      const years = (de - ds) / (365 * 24 * 3600 * 1000);
      this.formData.servicePeriodYears = Math.round(years * 10) / 10;
    },
    attachLimit(item) {
      if (item.limit != null) return item.limit;
      return item.multiple ? 8 : 1;
    },
    /** 详情不可传；新增且已达上限时不展示上传入口（删文件后可再传） */
    showContractAttachUploadBtn(item) {
      if (this.isDetails()) return false;
      const n = (this.attFiles[item.key] || []).filter((f) => f.url).length;
      return n < this.attachLimit(item);
    },
    beforeContractAttachUpload(file) {
      const name = (file && file.name) || "";
      const dot = name.lastIndexOf(".");
      const ext = dot >= 0 ? name.slice(dot + 1).toLowerCase() : "";
      if (!CONTRACT_ATTACH_EXT.has(ext)) {
        this.$message.error("仅支持上传图片、PDF、Word（.doc/.docx）");
        return false;
      }
      if (file && file.size > CONTRACT_ATTACH_MAX_SIZE_MB * 1024 * 1024) {
        this.$message.error("单个附件不能超过 " + CONTRACT_ATTACH_MAX_SIZE_MB + "MB");
        return false;
      }
      return true;
    },
    onAttachExceed(item) {
      const lim = this.attachLimit(item);
      this.$message.warning("「" + item.label + "」最多上传 " + lim + " 个文件");
    },
    /** 兼容 OSS 接口：data.link 或 data.data.link */
    pickContractUploadUrl(res) {
      if (!res || typeof res !== "object") return "";
      const d = res.data;
      if (d == null) return "";
      if (typeof d.link === "string" && d.link) return d.link;
      if (d.data && typeof d.data.link === "string" && d.data.link) return d.data.link;
      return "";
    },
    onContractAttachError() {
      this.$message.error("附件上传失败，请检查网络或文件大小后重试");
    },
    isSafeContractAttachmentUrl(url) {
      if (!url || typeof url !== "string") return false;
      const trimmed = url.trim();
      if (!trimmed) return false;
      return !/^(javascript|data|vbscript):/i.test(trimmed);
    },
    contractAttachmentFileName(file) {
      if (file && file.name) return file.name;
      if (file && file.url) {
        const clean = String(file.url).split("?")[0].split("#")[0];
        const name = clean.substring(clean.lastIndexOf("/") + 1);
        if (name) {
          try {
            return decodeURIComponent(name);
          } catch (e) {
            return name;
          }
        }
      }
      return "合同附件";
    },
    downloadContractAttachment(file) {
      const url = file && file.url ? String(file.url).trim() : "";
      if (!this.isSafeContractAttachmentUrl(url)) {
        this.$message.error("附件地址无效，无法下载");
        return;
      }
      const link = document.createElement("a");
      link.href = url;
      link.download = this.contractAttachmentFileName(file);
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onAttachSuccess(key, res, file, fileList, multiple) {
      const url = this.pickContractUploadUrl(res);
      if (url) this.$set(file, "url", url);
      else this.$message.error("上传成功但未返回文件地址，请重试或联系管理员");
      const mapped = (fileList || []).map((f) => ({
        name: f.name,
        url: f.url || this.pickContractUploadUrl(f.response),
        uid: f.uid || key + "-" + Date.now() + Math.random(),
      })).filter((f) => f.url);
      if (!multiple && mapped.length > 1) {
        this.$set(this.attFiles, key, [mapped[mapped.length - 1]]);
      } else {
        this.$set(this.attFiles, key, mapped);
      }
      this.syncAnnexAndExt();
    },
    onAttachRemove(key, file, fileList) {
      const mapped = (fileList || []).map((f) => ({
        name: f.name,
        url: f.url || this.pickContractUploadUrl(f.response),
        uid: f.uid,
      })).filter((f) => f.url);
      this.$set(this.attFiles, key, mapped);
      this.syncAnnexAndExt();
    },
    syncAnnexAndExt() {
      const urls = [];
      this.attachmentGroups.forEach((g) => {
        g.items.forEach((it) => {
          (this.attFiles[it.key] || []).forEach((f) => {
            if (f.url) urls.push(f.url);
          });
        });
      });
      this.formData.annexPath = urls.join(",");
      const plain = {};
      this.attachmentGroups.forEach((g) => {
        g.items.forEach((it) => {
          const arr = this.attFiles[it.key] || [];
          const u = arr.map((f) => f.url).filter(Boolean);
          plain[it.key] = it.multiple ? u : u[0] || "";
        });
      });
      this.formData.ext = JSON.stringify({ attachmentVersion: 1, attachments: plain });
    },
    hydrateAttachmentsFromDetail(d) {
      this.attFiles = {};
      if (d.ext && typeof d.ext === "string") {
        try {
          const j = JSON.parse(d.ext);
          if (j.attachments && typeof j.attachments === "object") {
            const attachments = { ...j.attachments };
            if (!attachments.legalIdCardFront && !attachments.legalIdCardBack && Array.isArray(attachments.legalIdCard)) {
              attachments.legalIdCardFront = attachments.legalIdCard[0] || "";
              attachments.legalIdCardBack = attachments.legalIdCard[1] || "";
            }
            Object.keys(attachments).forEach((k) => {
              const v = attachments[k];
              const urlArr = Array.isArray(v) ? v : v ? [v] : [];
              this.$set(
                this.attFiles,
                k,
                urlArr.map((url, i) => ({ name: k + "-" + (i + 1), url, uid: k + "-" + i }))
              );
            });
          }
        } catch (e) {
          /* ignore */
        }
      }
      const hasAny = Object.keys(this.attFiles).some((k) => (this.attFiles[k] || []).length);
      if (!hasAny && d.annexPath && typeof d.annexPath === "string") {
        const parts = d.annexPath.split(",").map((x) => x.trim()).filter(Boolean);
        if (parts.length) {
          this.$set(
            this.attFiles,
            "contractScan",
            parts.map((url, i) => ({ name: "附件" + (i + 1), url, uid: "legacy-" + i }))
          );
        }
      }
    },
    getDetail(id) {
      this.loading = true;
      getDetail(id)
        .then((res) => {
          const d = (res.data && res.data.data) || {};
          this.formData = {
            ...this.formData,
            ...d,
            list: Array.isArray(d.list)
              ? d.list.map((r) => {
                  const row = { ...this.emptyRow(), ...r };
                  if (row.taxRate != null && row.taxRate !== "") row.taxRate = Number(row.taxRate);
                  ["feeItem", "measureUnit", "settleMethod"].forEach((k) => {
                    if (row[k] != null && row[k] !== "") row[k] = Number(row[k]);
                  });
                  row.storeSubId = this.normalizeStackIds(row.storeSubId);
                  return row;
                })
              : [],
          };
          if (!this.formData.list.length) this.formData.list = [this.emptyRow()];
          if (this.formData.contractType != null && this.formData.contractType !== "") {
            const n = Number(this.formData.contractType);
            this.formData.contractType = Number.isFinite(n) ? n : null;
          }
          if (this.formData.copyCount != null && this.formData.copyCount !== "") {
            const n = Number(this.formData.copyCount);
            this.formData.copyCount = Number.isFinite(n) ? n : null;
          }
          {
            let sp = this.formData.servicePeriodYears;
            if ((sp == null || sp === "") && d.servicePeriodText != null && d.servicePeriodText !== "") {
              sp = d.servicePeriodText;
            }
            if (sp != null && sp !== "") {
              let n;
              if (typeof sp === "string" && /年/.test(sp)) {
                const m = String(sp).match(/([\d.]+)/);
                n = m ? Number(m[1]) : NaN;
              } else {
                n = Number(sp);
              }
              this.formData.servicePeriodYears = Number.isFinite(n) ? n : null;
            }
          }
          this.hydrateAttachmentsFromDetail(d);
          this.syncServicePeriod();
          this.loadUnboundStacks();
        })
        .finally(() => {
          this.loading = false;
        });
    },
    isDetails() {
      return this.transmitData.type === "view";
    },
    isRowTouched(row) {
      if (!row) return false;
      const keys = [
        "feeItem",
        "measureUnit",
        "storeSubId",
        "cargoNo",
        "cargoName",
        "contractWeight",
        "unitPrice",
        "amountExclTax",
        "amountInclTax",
        "lineRemark",
      ];
      return keys.some((k) => {
        const value = row[k];
        if (Array.isArray(value)) return value.length > 0;
        return value !== null && value !== undefined && value !== "";
      });
    },
    isStackSelectedByOtherRow(currentRow, stackId) {
      if (stackId == null || stackId === "") return false;
      return (this.formData.list || []).some((row) => {
        if (row === currentRow) return false;
        return this.normalizeStackIds(row.storeSubId).some((id) => String(id) === String(stackId));
      });
    },
    buildSubmitRows(rows) {
      const result = [];
      rows.forEach((row) => {
        if (!this.needStack(row)) {
          result.push({ ...row, storeSubId: null });
          return;
        }
        this.normalizeStackIds(row.storeSubId).forEach((id) => {
          const stack = this.findStackById(id);
          const item = { ...row, storeSubId: id };
          if (stack) {
            item.stockCode = stack.storeNo != null ? stack.storeNo : null;
            item.stockName = stack.storeName != null ? stack.storeName : null;
            item.locationCode = stack.locationCode != null ? stack.locationCode : null;
            item.locationName = stack.locationName != null ? stack.locationName : null;
            const area = this.stackArea(stack);
            if (area > 0) item.contractWeight = area;
          }
          result.push(item);
        });
      });
      return result;
    },
    toDateTime(dateStr) {
      if (!dateStr) return null;
      return dateStr.length <= 10 ? dateStr + " 00:00:00" : dateStr;
    },
    buildContractPayload(targetStatus, submitRows) {
      const payload = {
        contractName: this.formData.contractName,
        contractType: this.formData.contractType,
        copyCount: this.formData.copyCount,
        servicePeriodYears: this.formData.servicePeriodYears,
        custNo: this.formData.custNo,
        contractStartTime: this.toDateTime(this.formData.contractStartTime),
        contractEndTime: this.toDateTime(this.formData.contractEndTime),
        contractSignDate: this.formData.contractSignDate,
        annexPath: this.formData.annexPath,
        ext: this.formData.ext,
        businessType: this.formData.businessType || 1,
        status: targetStatus,
        list: submitRows,
      };
      if (this.transmitData.type === "edit") {
        payload.id = this.transmitData.id || this.formData.id;
      }
      return payload;
    },
    persistContract(payload) {
      return this.transmitData.type === "edit" ? update(payload) : add(payload);
    },
    saveDraft() {
      if (!this.formData.contractName) {
        this.$message.error("请输入合同名称");
        return;
      }
      if (!this.formData.custNo) {
        this.$message.error("请选择客户");
        return;
      }
      if (!this.formData.contractType) {
        this.$message.error("请选择合同类型");
        return;
      }
      const rows = (this.formData.list || []).filter((r) => this.isRowTouched(r));
      const submitRows = this.buildSubmitRows(rows);
      this.syncAnnexAndExt();
      const payload = this.buildContractPayload(CONTRACT_STATUS_DRAFT, submitRows);
      this.loading = true;
      this.persistContract(payload)
        .then(() => {
          this.$message.success("暂存成功");
          this.$emit("handleClose", "refresh");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    submitForm() {
      const continueSubmit = (valid) => {
        if (!valid) return;
        const rows = this.formData.list.filter((r) => this.isRowTouched(r));
        if (!rows.length) {
          this.$message.error("请至少录入一条结算明细（费目必填）");
          return;
        }
        const usedStackIds = new Set();
        for (const grp of this.attachmentGroups) {
          for (const it of grp.items) {
            if (!it.required) continue;
            const arr = (this.attFiles[it.key] || []).filter((f) => f.url);
            const min = it.minCount != null ? it.minCount : 1;
            if (arr.length < min) {
              const extra = min > 1 ? "（须 " + min + " 个文件）" : "";
              this.$message.error("请上传必传附件：「" + it.label + "」" + extra);
              return;
            }
          }
        }
        for (const row of rows) {
          if (row.feeItem == null || row.feeItem === "") {
            this.$message.error("存在未选择费目的结算明细，请补充或删除该行");
            return;
          }
          if (!this.isFeeAllowedForCurrentContract(row.feeItem)) {
            this.$message.error("「" + this.feeLabel(row.feeItem) + "」不适用于当前合同类型");
            return;
          }
          if (row.measureUnit == null || row.measureUnit === "") {
            this.$message.error("请为每条明细选择计量单位");
            return;
          }
          if (this.needCoal(row) && (!row.cargoNo || !row.cargoName)) {
            this.$message.error("请为「" + this.feeLabel(row.feeItem) + "」选择煤种（品名）");
            return;
          }
          const stackIds = this.normalizeStackIds(row.storeSubId);
          if (this.needStack(row) && !stackIds.length) {
            this.$message.error("仓储服务费须选择堆位");
            return;
          }
          if (this.needStack(row)) {
            for (const id of stackIds) {
              const key = String(id);
              if (usedStackIds.has(key)) {
              this.$message.error("同一合同中仓储服务费堆位不能重复选择");
                return;
              }
              usedStackIds.add(key);
            }
          }
          if (row.unitPrice == null || row.contractWeight == null || row.taxRate == null) {
            this.$message.error("单价、数量、税率为必填");
            return;
          }
          const price = Number(row.unitPrice);
          const weight = Number(row.contractWeight);
          if (!Number.isFinite(price) || price <= 0 || !Number.isFinite(weight) || weight <= 0) {
            this.$message.error("单价和数量必须大于0");
            return;
          }
          const unitPrice = Number(row.unitPrice);
          const contractWeight = Number(row.contractWeight);
          if (!Number.isFinite(unitPrice) || unitPrice < 0 || !Number.isFinite(contractWeight) || contractWeight <= 0) {
            this.$message.error("单价须大于等于0，数量须大于0");
            return;
          }
          const tr = Number(row.taxRate);
          if (!this.isSupportedTaxRate(tr)) {
            this.$message.error("税率只能选择6‰或13‰");
            return;
          }
          this.recalcSettleRow(row);
          if (row.amountExclTax == null || row.amountInclTax == null || row.settleMethod == null || row.settleMethod === "") {
            this.$message.error("请完善单价、数量与税率，系统将自动计算金额；结算方式须选择");
            return;
          }
        }
        const submitRows = this.buildSubmitRows(rows);
        this.syncAnnexAndExt();
        const payload = this.buildContractPayload(CONTRACT_STATUS_ACTIVE, submitRows);
        this.loading = true;
        this.persistContract(payload)
          .then(() => {
            this.$message.success("保存成功");
            this.$emit("handleClose", "refresh");
          })
          .finally(() => {
            this.loading = false;
          });
      };
      this.$refs.custAvueForm.validate((valid, done) => {
        if (typeof done === "function") done();
        if (!valid) return;
        this.$refs.mainForm.validate(continueSubmit);
      });
    },
  },
};
</script>

<style scoped lang="scss">
.tc-form-wrap {
  max-height: 70vh;
  overflow-y: auto;
  padding-right: 8px;
}
.tc-cust-avue ::v-deep .avue-form__menu {
  display: none;
}
.tc-cust-avue ::v-deep .el-form-item {
  margin-bottom: 18px;
}
.tc-sec-title {
  margin: 16px 0 12px;
  font-size: 15px;
  font-weight: 600;
  border-left: 3px solid #409eff;
  padding-left: 10px;
}
.tc-table-actions {
  margin-bottom: 8px;
}
.tc-settle-table {
  width: 100%;
  margin-bottom: 16px;
}
.tc-money-cell {
  font-variant-numeric: tabular-nums;
}
.tc-stack-cell {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
}
.tc-stack-summary {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #303133;
}
.tc-stack-cell + .el-select {
  display: none;
}
.tc-stack-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 12px;
}
.tc-stack-filter {
  width: 180px;
}
.tc-stack-search {
  width: 260px;
}
.tc-stack-total {
  margin-left: auto;
  font-size: 13px;
  color: #606266;
}
.tc-stack-table ::v-deep .el-table__body-wrapper {
  overflow-x: hidden;
}
.tc-upload-group {
  margin-bottom: 20px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #ebeef5;
}
.tc-upload-group-title {
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 12px;
  color: #303133;
}
/* flex 行：避免某一格被文件列表撑高后，浮动布局把后续格子挤乱 */
.tc-upload-row.el-row--flex {
  flex-wrap: wrap;
  align-items: flex-start;
}
.tc-upload-col {
  margin-bottom: 12px;
}
.tc-upload-card {
  background: #fff;
  border: 1px dashed #dcdfe6;
  border-radius: 8px;
  padding: 10px;
  box-sizing: border-box;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.tc-upload-card ::v-deep .el-upload {
  width: 100%;
  min-width: 0;
}
/* 文件列表限高滚动，避免单格无限增高 */
.tc-upload-card ::v-deep .el-upload-list {
  max-height: 140px;
  overflow-y: auto;
  margin-top: 6px;
}
.tc-upload-card ::v-deep .el-upload-list__item {
  margin-top: 4px;
}
.tc-upload-card ::v-deep .el-upload-list__item-name {
  word-break: break-all;
  white-space: normal;
  line-height: 1.35;
  max-width: 100%;
}
.tc-upload-card ::v-deep .el-upload-list__item-name:hover {
  color: #409eff;
  cursor: pointer;
}
.tc-upload-label {
  font-size: 12px;
  color: #606266;
  margin-bottom: 8px;
}
/* 无上传按钮时隐藏 file input，避免残留「选择文件」可点区域（列表删除仍可用） */
.tc-upload--hide-trigger ::v-deep .el-upload__input {
  display: none !important;
}
.tc-req {
  color: #f56c6c;
  margin-left: 4px;
}
.tc-del {
  color: #f56c6c;
}
.tc-footer {
  margin-top: 16px;
  text-align: right;
}
</style>
